import {PIXELS_PER_METER} from "@/dungeons/geometry/Coordinates.ts";
import {hasLineOfSight, pointInVisibility, type Point} from "./VisibilityGeometry.ts";
import type {Visibility} from "./VisibilityModel.ts";
import {perceivesToken, perceptionProfile} from "./PerceptionProfile.ts";
import {illuminationAt} from "@/dungeons/lighting/LightingModel.ts";

const EPSILON = 1e-7;
export const MIN_TOKEN_ILLUMINATION = 0.1;

export function pointInTokenSight(point: Point, visibility: Visibility): boolean {
    // Illumination is observer-independent. Resolve it at most once, only for ordinary sight.
    let illuminated: boolean | undefined;
    return visibility.areas.some(area => {
        const profile = area.perception ?? {...perceptionProfile({schemaVersion: 1,
            passivePerception: null, specialSenses: []}), normalRangeMeters: visibility.radiusMeters,
            geometryRangeMeters: Math.max(3, visibility.radiusMeters)};
        const dx = point.x - area.origin.x;
        const dy = point.y - area.origin.y;
        const distanceSquared = dx * dx + dy * dy;
        if (distanceSquared > (profile.geometryRangeMeters * PIXELS_PER_METER) ** 2 || area.polygon.length < 3) return false;
        if (visibility.staleGeometry && !pointInVisibility(point, [area.polygon])) return false;
        const heading = area.rotationDegrees * Math.PI / 180;
        const query = {distanceMeters: Math.sqrt(distanceSquared) / PIXELS_PER_METER,
            directionCosine: distanceSquared > EPSILON
                ? (dx * Math.cos(heading) + dy * Math.sin(heading)) / Math.sqrt(distanceSquared) : 1,
            lineOfSight: hasLineOfSight(area.origin, point, visibility.blockers),
            illuminated: false};
        if (perceivesToken(profile, query)) return true;
        if (!perceivesToken(profile, {...query, illuminated: true})) return false;
        illuminated ??= visibility.lighting
            ? illuminationAt(point, {...visibility.lighting, lights: visibility.lights}, 0, false).brightness >= MIN_TOKEN_ILLUMINATION
            : true;
        return illuminated;
    });
}
